import React, { useState, useRef, useEffect } from 'react';
import './TrueFalseExercise.css';

const MAX_STATEMENTS = 10;

const TFIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M9 12l2 2 4-4"/>
  </svg>
);

const EditIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TrueFalseExercise = ({ statements, title, level, alwaysShowAnswers = false, onClose, updateAttributes, isStudent = false, myState = {}, otherState = {}, updateState = () => {} }) => {
  const [currentStatementIndex, setCurrentStatementIndex] = useState(0);
  const [showAnswers, setShowAnswers] = useState(false);
  const [userAnswers, setUserAnswers] = useState({});
  const [teacherShowsExplanation, setTeacherShowsExplanation] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(null);
  const syncedRef = React.useRef(false);
  const prevResetAtRef = useRef(null);

  const [isEditing, setIsEditing] = useState(false);
  const [editableTitle, setEditableTitle] = useState(title);
  const [editableStatements, setEditableStatements] = useState([...statements]);
  const [editableAlwaysShowAnswers, setEditableAlwaysShowAnswers] = useState(alwaysShowAnswers);
  const addBtnRef = useRef(null);
  const dotsScrollRef = useRef(null);
  const hintRunRef = useRef(false);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  useEffect(() => {
    if (syncedRef.current) return;
    const hasAnswers = myState.answers && Object.keys(myState.answers).length > 0;
    const hasResults = myState.showResults === true;
    if (hasAnswers || hasResults) {
      syncedRef.current = true;
      if (myState.answers) {
        setUserAnswers(myState.answers);
      }
      if (hasResults) {
        setShowResults(true);
        if (typeof myState.score === 'number') setScore(myState.score);
      }
    }
  }, [myState.answers, myState.showResults, myState.score]); // eslint-disable-line react-hooks/exhaustive-deps

  // Watch other side's showResults → trigger local results
  useEffect(() => {
    if (!otherState.showResults) return;
    const answersToScore = { ...(otherState.answers || {}), ...userAnswers };
    const correctCount = Object.keys(answersToScore).filter(
      idx => answersToScore[idx] === editableStatements[parseInt(idx)]?.isTrue
    ).length;
    setScore(correctCount);
    setShowResults(true);
  }, [otherState.showResults]); // eslint-disable-line

  // Watch other side's resetAt → reset local state
  useEffect(() => {
    const resetAt = otherState.resetAt;
    if (!resetAt || resetAt === prevResetAtRef.current) return;
    prevResetAtRef.current = resetAt;
    setUserAnswers({});
    setShowAnswers(false);
    setShowResults(false);
    setScore(null);
    setCurrentStatementIndex(0);
    setTeacherShowsExplanation({});
    updateState({ answers: {}, showResults: false, score: 0, showExplanation: {} });
  }, [otherState.resetAt]); // eslint-disable-line

  // Watch other side's currentStatement → follow navigation
  useEffect(() => {
    if (typeof otherState.currentStatement !== 'number') return;
    setCurrentStatementIndex(otherState.currentStatement);
    const hasAnswer = !isStudent
      ? (otherState.answers?.[otherState.currentStatement] !== undefined)
      : (userAnswers[otherState.currentStatement] !== undefined);
    setShowAnswers(hasAnswer);
  }, [otherState.currentStatement]); // eslint-disable-line

  const handleAnswerChange = (statementIndex, answer) => {
    setUserAnswers(prev => {
      const next = { ...prev, [statementIndex]: answer };
      updateState({ answers: next });
      return next;
    });
    setShowAnswers(true);
  };

  const handleNext = () => {
    if (currentStatementIndex < editableStatements.length - 1) {
      const nextIdx = currentStatementIndex + 1;
      setCurrentStatementIndex(nextIdx);
      updateState({ currentStatement: nextIdx });
      if (userAnswers[nextIdx] === undefined) {
        setShowAnswers(false);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStatementIndex > 0) {
      const prevIdx = currentStatementIndex - 1;
      setCurrentStatementIndex(prevIdx);
      updateState({ currentStatement: prevIdx });
      if (userAnswers[prevIdx] === undefined) {
        setShowAnswers(false);
      }
    }
  };

  const handleSave = () => {
    setIsEditing(false);
    if (updateAttributes) {
      updateAttributes({
        statements: editableStatements,
        title: editableTitle,
        level,
        alwaysShowAnswers: editableAlwaysShowAnswers,
      });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditableTitle(title);
    setEditableStatements([...statements]);
    setEditableAlwaysShowAnswers(alwaysShowAnswers);
  };

  const updateStatementField = (index, field, value) => {
    setEditableStatements(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
  };

  const handleAddStatement = () => {
    if (editableStatements.length >= MAX_STATEMENTS) return;
    const newStatement = { statement: '', isTrue: true };
    setEditableStatements(prev => {
      const updated = [...prev, newStatement];
      setCurrentStatementIndex(updated.length - 1);
      return updated;
    });
  };

  const handleDeleteStatement = (statementIndex) => {
    if (editableStatements.length <= 1) return;

    setEditableStatements(prev => {
      const updated = prev.filter((_, index) => index !== statementIndex);
      if (currentStatementIndex >= statementIndex) {
        setCurrentStatementIndex(Math.max(0, currentStatementIndex - 1));
      }
      return updated;
    });
  };

  const handleTryAgain = () => {
    setUserAnswers({});
    setShowAnswers(false);
    setShowResults(false);
    setScore(null);
    setCurrentStatementIndex(0);
    setTeacherShowsExplanation({});
    updateState({ answers: {}, showResults: false, score: 0, showExplanation: {}, resetAt: Date.now() });
  };

  const handleFinish = () => {
    const answersToScore = { ...(otherState.answers || {}), ...userAnswers };
    const correctCount = Object.keys(answersToScore).filter(
      idx => answersToScore[idx] === editableStatements[parseInt(idx)]?.isTrue
    ).length;
    setScore(correctCount);
    setShowResults(true);
    updateState({ showResults: true, score: correctCount, answers: userAnswers });
  };

  // микро-хинт прокрутки как в ABCD (один раз)
  useEffect(() => {
    if (hintRunRef.current) return;
    if (!dotsScrollRef.current) return;
    if (editableStatements.length <= 5) return;
    hintRunRef.current = true;
    const scroller = dotsScrollRef.current;
    const delta = 24; // px
    try {
      const start = scroller.scrollLeft;
      const t1 = setTimeout(() => {
        scroller.scrollTo({ left: start + delta, behavior: 'smooth' });
        const t2 = setTimeout(() => {
          scroller.scrollTo({ left: start, behavior: 'smooth' });
        }, 500);
        const cleanup = () => clearTimeout(t2);
        (hintRunRef.currentCleanup = cleanup);
      }, 120);
      return () => {
        clearTimeout(t1);
        if (hintRunRef.currentCleanup) hintRunRef.currentCleanup();
      };
    } catch (_) {
      // no-op
    }
  }, [editableStatements.length]);

  // Update gradient fades based on scroll position
  useEffect(() => {
    const el = dotsScrollRef.current;
    if (!el) return;
    const update = () => {
      const max = el.scrollWidth - el.clientWidth;
      setShowLeftFade(el.scrollLeft > 2);
      setShowRightFade(el.scrollLeft < max - 2);
    };
    update();
    el.addEventListener('scroll', update);
    return () => el.removeEventListener('scroll', update);
  }, [editableStatements.length]);

  const perDot = 40; // px
  const gap = 8; // px
  const visibleSlots = Math.min(editableStatements.length, 5);
  const scrollWidth = visibleSlots > 0 ? Math.round((visibleSlots * perDot) + ((visibleSlots - 1) * gap)) : 0;

  const displayAnswers = userAnswers;
  const displayAnswer = displayAnswers[currentStatementIndex];
  const otherAnswer = otherState.answers?.[currentStatementIndex];
  const canShowResults = !showResults && !isEditing && editableStatements.every((_, i) =>
    userAnswers[i] !== undefined || (otherState.answers || {})[i] !== undefined
  );
  const displayScore = score;
  const current = editableStatements[currentStatementIndex];

  // Results panel data — same ring/tier/headline pattern as the other
  // redesigned exercises, re-skinned with this exercise's green accent.
  let resultsData = null;
  if (showResults && displayScore !== null) {
    const total = editableStatements.length || 1;
    const percent = Math.round((displayScore / total) * 100);
    const incorrect = total - displayScore;
    const circumference = 2 * Math.PI * 42;
    const dashOffset = circumference * (1 - percent / 100);
    let tier = 'practice';
    let headline;
    let subline;
    if (percent >= 90) { tier = 'excellent'; headline = 'Outstanding!'; subline = isStudent ? 'You nailed it.' : 'Student nailed it.'; }
    else if (percent >= 70) { tier = 'great'; headline = 'Great job!'; subline = 'Solid performance.'; }
    else if (percent >= 50) { tier = 'good'; headline = 'Good work.'; subline = isStudent ? 'Keep going — you’re close.' : 'Getting there.'; }
    else { tier = 'practice'; headline = 'Keep practicing'; subline = isStudent ? 'You’ll get it — try again.' : 'Review and try again.'; }
    resultsData = { percent, incorrect, circumference, dashOffset, tier, headline, subline };
  }

  return (
    <div className="tf-exercise">
      {/* Header */}
      <div className="tf-header">
        <div className="tf-header-left">
          <div className="tf-header-icon"><TFIcon /></div>
          {isEditing ? (
            <input
              className="tf-title-input"
              value={editableTitle}
              onChange={e => setEditableTitle(e.target.value)}
            />
          ) : (
            <span className="tf-title">{editableTitle}</span>
          )}
          <span className="tf-level-badge">{level}</span>
        </div>
        <div className="tf-header-actions">
          {!isEditing && !isStudent && (
            <button className="tf-icon-btn" aria-label="Edit" data-tip="Edit" onClick={() => setIsEditing(true)}>
              <EditIcon />
            </button>
          )}
          {isEditing && (
            <>
              <button className="tf-icon-btn is-save" aria-label="Save" data-tip="Save" onClick={handleSave}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="9,12 11,14 15,10" />
                </svg>
              </button>
              <button className="tf-icon-btn is-cancel" aria-label="Cancel" data-tip="Cancel" onClick={handleCancel}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </>
          )}
          {onClose && (
            <button className="tf-icon-btn" onClick={onClose} aria-label="Close" data-tip="Close">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="eye-icon">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-open" />
                <circle cx="12" cy="12" r="3" className="eye-pupil" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="tf-body">
        {current && (
          <div className="tf-statement-card">
            <div className="tf-statement-chip">
              Statement {currentStatementIndex + 1} of {editableStatements.length}
            </div>
            <div className="tf-statement-text">
              {isEditing ? (
                <input
                  type="text"
                  value={current.statement || ''}
                  onChange={e => updateStatementField(currentStatementIndex, 'statement', e.target.value)}
                  className="tf-statement-input"
                  placeholder="Enter statement..."
                />
              ) : (
                current.statement || ''
              )}
            </div>

            {!isEditing && (
              <div className="tf-options">
                <button
                  className={`tf-option-btn is-true${
                    (displayAnswer === true || otherAnswer === true)
                      ? (current.isTrue === true ? ' correct-indicator' : ' wrong-indicator')
                      : ''
                  }`}
                  onClick={() => handleAnswerChange(currentStatementIndex, true)}
                  disabled={showAnswers || otherAnswer !== undefined}
                >
                  True
                </button>
                <button
                  className={`tf-option-btn is-false${
                    (displayAnswer === false || otherAnswer === false)
                      ? (current.isTrue === false ? ' correct-indicator' : ' wrong-indicator')
                      : ''
                  }`}
                  onClick={() => handleAnswerChange(currentStatementIndex, false)}
                  disabled={showAnswers || otherAnswer !== undefined}
                >
                  False
                </button>
              </div>
            )}

            {isEditing && (
              <div className="tf-edit-answer">
                <label className="tf-edit-answer-label">Correct Answer:</label>
                <div className="tf-edit-radio-group">
                  <label className="tf-edit-radio">
                    <input
                      type="radio"
                      name={`correct-${currentStatementIndex}`}
                      checked={current.isTrue === true}
                      onChange={() => updateStatementField(currentStatementIndex, 'isTrue', true)}
                    />
                    True
                  </label>
                  <label className="tf-edit-radio">
                    <input
                      type="radio"
                      name={`correct-${currentStatementIndex}`}
                      checked={current.isTrue === false}
                      onChange={() => updateStatementField(currentStatementIndex, 'isTrue', false)}
                    />
                    False
                  </label>
                </div>
                <label className="tf-always-show">
                  <input
                    type="checkbox"
                    checked={editableAlwaysShowAnswers}
                    onChange={e => setEditableAlwaysShowAnswers(e.target.checked)}
                  />
                  <span>Always show answers to student</span>
                </label>
              </div>
            )}

            {!isEditing && !isStudent && (
              <div className="tf-teacher-hint" role="note" aria-label="Only visible to teacher">
                <span className="tf-teacher-hint-badge" aria-hidden>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a19.79 19.79 0 0 1 5.06-5.94M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a19.79 19.79 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                  Only visible to you
                </span>
                <span className="tf-teacher-hint-answer">
                  Correct: <strong>{current.isTrue ? 'True' : 'False'}</strong>
                </span>
              </div>
            )}

            {!isEditing && (
              !isStudent
              || (showAnswers && (alwaysShowAnswers || !!otherState.showExplanation?.[currentStatementIndex]))
            ) && (
              <div className="tf-explanation-box">
                <div className="tf-explanation-label">Explanation:</div>
                <p className="tf-explanation-text">
                  {current.explanation && current.explanation.trim().length > 0
                    ? current.explanation
                    : <>The correct answer is <strong>{current.isTrue ? 'True' : 'False'}</strong> based on the text.</>}
                </p>
                {!isStudent && (
                  <label className="tf-reveal-toggle">
                    <input
                      type="checkbox"
                      checked={!!teacherShowsExplanation[currentStatementIndex]}
                      onChange={e => {
                        const next = { ...teacherShowsExplanation, [currentStatementIndex]: e.target.checked };
                        setTeacherShowsExplanation(next);
                        updateState({ showExplanation: next });
                      }}
                    />
                    <span>Show to student</span>
                  </label>
                )}
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="navigation-buttons">
          {currentStatementIndex > 0 ? (
            <button className="nav-btn prev-btn" onClick={handlePrevious}>Previous</button>
          ) : <div className="tf-nav-placeholder" />}

          <div className="question-dots">
            <div
              ref={dotsScrollRef}
              className="question-dots-scroll"
              style={{
                maxWidth: scrollWidth,
                WebkitMaskImage: (() => {
                  const pad = 28;
                  if (showLeftFade && showRightFade) {
                    return `linear-gradient(to right, rgba(0,0,0,0) 0px, #000 ${pad}px, #000 calc(100% - ${pad}px), rgba(0,0,0,0) 100%)`;
                  }
                  if (showLeftFade && !showRightFade) {
                    return `linear-gradient(to right, rgba(0,0,0,0) 0px, #000 ${pad}px, #000 100%)`;
                  }
                  if (!showLeftFade && showRightFade) {
                    return `linear-gradient(to right, #000 0%, #000 calc(100% - ${pad}px), rgba(0,0,0,0) 100%)`;
                  }
                  return 'none';
                })(),
                maskImage: (() => {
                  const pad = 28;
                  if (showLeftFade && showRightFade) {
                    return `linear-gradient(to right, rgba(0,0,0,0) 0px, #000 ${pad}px, #000 calc(100% - ${pad}px), rgba(0,0,0,0) 100%)`;
                  }
                  if (showLeftFade && !showRightFade) {
                    return `linear-gradient(to right, rgba(0,0,0,0) 0px, #000 ${pad}px, #000 100%)`;
                  }
                  if (!showLeftFade && showRightFade) {
                    return `linear-gradient(to right, #000 0%, #000 calc(100% - ${pad}px), rgba(0,0,0,0) 100%)`;
                  }
                  return 'none';
                })()
              }}
            >
              {editableStatements.map((_, index) => {
                const answersForDots = { ...(otherState.answers || {}), ...userAnswers };
                let dotClass = 'dot';
                if (answersForDots[index] !== undefined) {
                  dotClass += answersForDots[index] === editableStatements[index]?.isTrue ? ' dot-correct' : ' dot-incorrect';
                }
                if (index === currentStatementIndex) dotClass += ' active';
                return (
                  <div key={index} className="tf-dot-wrap">
                    <button
                      className={dotClass}
                      onClick={() => {
                        setCurrentStatementIndex(index);
                        updateState({ currentStatement: index });
                        setShowAnswers(userAnswers[index] !== undefined);
                      }}
                    >
                      {index + 1}
                    </button>
                    {isEditing && editableStatements.length > 1 && (
                      <button
                        className="tf-dot-delete"
                        onClick={(e) => { e.stopPropagation(); handleDeleteStatement(index); }}
                        title="Delete statement"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {isEditing && editableStatements.length < MAX_STATEMENTS && (
              <button ref={addBtnRef} className="dot add-dot" onClick={handleAddStatement} title="Add statement" type="button">
                +
              </button>
            )}
          </div>

          {canShowResults ? (
            <button className="tf-submit-btn" onClick={handleFinish}>Show Results</button>
          ) : currentStatementIndex < editableStatements.length - 1 ? (
            <button className="nav-btn next-btn" onClick={handleNext}>Next</button>
          ) : <div className="tf-nav-placeholder" />}
        </div>

        {isEditing && editableStatements.length >= MAX_STATEMENTS && (
          <div className="tf-max-hint">Maximum of {MAX_STATEMENTS} statements per exercise</div>
        )}

        {/* Results panel */}
        {resultsData && (
          <div className={`tf-results tier-${resultsData.tier}`}>
            <div className="tf-results-ring-wrap">
              <svg viewBox="0 0 100 100" className="tf-results-ring" aria-hidden>
                <circle cx="50" cy="50" r="42" className="tf-results-ring-track" />
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  className="tf-results-ring-progress"
                  style={{ strokeDasharray: resultsData.circumference, strokeDashoffset: resultsData.dashOffset }}
                />
              </svg>
              <div className="tf-results-ring-center">
                <div className="tf-results-percent">{resultsData.percent}%</div>
                <div className="tf-results-percent-label">score</div>
              </div>
            </div>
            <div className="tf-results-headline">{resultsData.headline}</div>
            <div className="tf-results-subline">{resultsData.subline}</div>
            <div className="tf-results-stats">
              <div className="tf-results-stat is-correct">
                <span className="tf-results-stat-icon" aria-hidden>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </span>
                <span className="tf-results-stat-value">{displayScore}</span>
                <span className="tf-results-stat-label">correct</span>
              </div>
              <div className="tf-results-stats-divider" aria-hidden />
              <div className="tf-results-stat is-incorrect">
                <span className="tf-results-stat-icon" aria-hidden>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </span>
                <span className="tf-results-stat-value">{resultsData.incorrect}</span>
                <span className="tf-results-stat-label">incorrect</span>
              </div>
            </div>
            <button className="tf-results-cta" onClick={handleTryAgain}>
              <span>Try Again</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <polyline points="23 4 23 10 17 10"/>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrueFalseExercise;
